// @flow weak

import localforage from 'localforage';
import { parse, stringify } from 'query-string';
import * as R from 'ramda';
import * as AceSdk from '@ace-auth/sdk-browser';

import { ApolloService, HttpService, TokenService } from '~/services';
import {
  isReactNativeAceWebView,
  renameTokens,
  STORAGE_KEY_AUTH_TOKENS,
  STORAGE_KEY_ELEVATE_TOKENS,
  STORAGE_KEY_LOCALE,
  STORAGE_KEY_REDIRECT_URI,
} from '~/helpers';
import { ELEVATE_TOKENS_MUTATION } from '~/gql';

const {
  REACT_APP_ACE_ENDPOINT,
  REACT_APP_CLIENT_ID,
  REACT_APP_ACE_SSO_ENDPOINT,
  REACT_APP_MS_PLATFORM_ENDPOINT,
  REACT_APP_MS_LANDING_ENDPOINT,
  REACT_APP_REACT_NATIVE_WEBVIEW_ACE_SIGN_IN_SUCCESS_REDIRECT_ENDPOINT,
  REACT_APP_REACT_NATIVE_WEBVIEW_ACE_SIGN_OUT_SUCCESS_REDIRECT_ENDPOINT,
  REACT_APP_REACT_NATIVE_WEBVIEW_ACE_SIGN_UP_SUCCESS_REDIRECT_ENDPOINT,
} = process.env;

const callToAce = async method => {
  await localforage.setItem(STORAGE_KEY_REDIRECT_URI, window.location.pathname);

  const props = {
    client_id: REACT_APP_CLIENT_ID,
    redirect_uri: window.location.origin,
    locale: mapLocale(await getLocale()),
  };

  window.location.href = `${
    // $FlowIssue
    REACT_APP_ACE_ENDPOINT
  }/${method}?${stringify(props)}`;
};

const getLocale = async () =>
  (await localforage.getItem(STORAGE_KEY_LOCALE)) || 'en';

const mapLocale = R.cond([
  [R.equals('en'), R.always('en-US')],
  [R.T, R.identity],
]);

const AceService = new AceSdk.CreateClient({
  aceEndpoint: REACT_APP_ACE_ENDPOINT,
  clientId: REACT_APP_CLIENT_ID,
  ownAceEndpoint: REACT_APP_ACE_SSO_ENDPOINT,
  signInRedirectUri: isReactNativeAceWebView
    ? REACT_APP_REACT_NATIVE_WEBVIEW_ACE_SIGN_IN_SUCCESS_REDIRECT_ENDPOINT
    : REACT_APP_MS_PLATFORM_ENDPOINT,
  signUpRedirectUri: isReactNativeAceWebView
    ? REACT_APP_REACT_NATIVE_WEBVIEW_ACE_SIGN_UP_SUCCESS_REDIRECT_ENDPOINT
    : REACT_APP_MS_PLATFORM_ENDPOINT,
  storage: localforage,
  storageKey: STORAGE_KEY_AUTH_TOKENS,
  autorefresh: true,
  flags: {
    nosso: isReactNativeAceWebView,
  },
});

export const authorize = async () => {
  const { search } = window.location;

  if (search) {
    const tokens = R.pick(['access_token', 'id_token'], parse(search));
    const isTokenElevated = TokenService.isTokenElevated(tokens.id_token);

    if (isTokenElevated) {
      await ApolloService.client.mutate({
        mutation: ELEVATE_TOKENS_MUTATION,
        variables: {
          values: {
            accessToken: tokens.access_token,
            idToken: tokens.id_token,
          },
        },
      });

      return tokens;
    }
  }

  return getAuthTokens();
};

export const getAuthTokens = async () => {
  try {
    const authTokens = await AceService.getTokens();

    if (authTokens) {
      HttpService.setAuthorizationToken(authTokens.access_token);

      return renameTokens(authTokens);
    }
  } catch (e) {}

  HttpService.removeAuthorizationToken();

  return null;
};

export const getElevateTokens = async () => {
  const elevateTokens = await localforage.getItem(STORAGE_KEY_ELEVATE_TOKENS);

  if (!elevateTokens) {
    return;
  }

  const isTokenExpired = TokenService.isTokenExpired(elevateTokens.accessToken);

  if (isTokenExpired) {
    await localforage.removeItem(STORAGE_KEY_ELEVATE_TOKENS);

    return;
  }

  return elevateTokens;
};

export const setElevateTokens = async elevateTokens => {
  if (!elevateTokens || R.isEmpty(elevateTokens)) {
    await localforage.removeItem(STORAGE_KEY_ELEVATE_TOKENS);

    return;
  }

  const isTokenExpired = TokenService.isTokenExpired(elevateTokens.accessToken);

  if (isTokenExpired) {
    await localforage.removeItem(STORAGE_KEY_ELEVATE_TOKENS);

    return;
  }

  await localforage.setItem(STORAGE_KEY_ELEVATE_TOKENS, elevateTokens);
};

export const signIn = async () => {
  AceService.setLocale(await getLocale());

  // ace returns tokens if user is signed in
  return await AceService.signIn();
};

export const signOut = async () => {
  const signOutUri = isReactNativeAceWebView
    ? REACT_APP_REACT_NATIVE_WEBVIEW_ACE_SIGN_OUT_SUCCESS_REDIRECT_ENDPOINT
    : `${REACT_APP_MS_LANDING_ENDPOINT}?${stringify({
        locale: await getLocale(),
      })}`;

  AceService.setSignOutRedirectUri(signOutUri);
  await Promise.all([
    localforage.removeItem(STORAGE_KEY_AUTH_TOKENS),
    localforage.removeItem(STORAGE_KEY_ELEVATE_TOKENS),
  ]);
  await AceService.signOut();
};

export const signUp = async () => {
  AceService.setLocale(await getLocale());
  await AceService.signUp();
};

export const elevate = async () => await callToAce('elevate');
export const changeEmail = async () => await callToAce('email-change');
export const changePassword = async () => await callToAce('password-change');
export const setupMfa = async () => await callToAce('setup-mfa');
